import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Shield, 
  Package, 
  Users, 
  CreditCard, 
  DollarSign,
  Truck,
  CheckCircle,
  LogOut,
  X,
  Download,
  Plus,
  Edit,
  Trash2,
  Link2,
  User,
  ExternalLink,
  Copy
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Stats Card Component
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="glass rounded-2xl p-6">
    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-4`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <p className="text-slate-400 text-sm">{label}</p>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

// Shipping Update Modal
const ShippingModal = ({ isOpen, order, onClose, onUpdate }) => {
  const [status, setStatus] = useState(order?.shippingStatus || 'pending');
  const [trackingNumber, setTrackingNumber] = useState(order?.trackingNumber || '');
  const [carrier, setCarrier] = useState(order?.carrier || '');
  const [paymentStatus, setPaymentStatus] = useState(order?.paymentStatus || 'pending');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onUpdate(order.id, { status, trackingNumber, carrier, paymentStatus });
      onClose();
    } catch (error) {
      toast.error('Failed to update order');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay flex items-center justify-center p-4">
      <motion.div
        className="glass rounded-3xl p-8 w-full max-w-md"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Update Order</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Shipping Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="input-field w-full"
            >
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="in_transit">In Transit</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Payment Status</label>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="input-field w-full"
            >
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="cash">Cash on Delivery</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Tracking Number</label>
            <Input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              className="input-field"
              placeholder="Enter tracking number"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Carrier</label>
            <Input
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              className="input-field"
              placeholder="e.g., Delhivery, BlueDart"
            />
          </div>

          <Button type="submit" className="w-full btn-primary" disabled={loading}>
            {loading ? 'Updating...' : 'Update Order'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

// Create Card Modal
const CreateCardModal = ({ isOpen, users, onClose, onCreate }) => {
  const [userId, setUserId] = useState('');
  const [linkedUrl, setLinkedUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      toast.error('Please select a user');
      return;
    }
    setLoading(true);
    try {
      await onCreate({ userId, linkedUrl: linkedUrl || null });
      setUserId('');
      setLinkedUrl('');
      onClose();
    } catch (error) {
      toast.error('Failed to create card');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay flex items-center justify-center p-4">
      <motion.div
        className="glass rounded-3xl p-8 w-full max-w-md"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Assign Card to User</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Select User</label>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="input-field w-full"
              required
            >
              <option value="">Choose a user...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name || user.username} ({user.username})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Linked URL (Optional)</label>
            <Input
              value={linkedUrl}
              onChange={(e) => setLinkedUrl(e.target.value)}
              className="input-field"
              placeholder="https://example.com or leave empty"
            />
            <p className="text-slate-500 text-xs mt-1">User can update this later from their dashboard</p>
          </div>

          <Button type="submit" className="w-full btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Card'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

// Edit Card Modal
const EditCardModal = ({ isOpen, card, users, onClose, onUpdate }) => {
  const [linkedUrl, setLinkedUrl] = useState(card?.linkedUrl || '');
  const [userId, setUserId] = useState(card?.userId || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (card) {
      setLinkedUrl(card.linkedUrl || '');
      setUserId(card.userId || '');
    }
  }, [card]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onUpdate(card.id, { linkedUrl: linkedUrl || null, userId });
      onClose();
    } catch (error) {
      toast.error('Failed to update card');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !card) return null;

  return (
    <div className="modal-overlay flex items-center justify-center p-4">
      <motion.div
        className="glass rounded-3xl p-8 w-full max-w-md"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Edit Card</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-slate-800/50 rounded-xl">
          <p className="text-slate-400 text-sm">Card Code</p>
          <p className="text-white font-mono">{card.urlCode}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Assigned User</label>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="input-field w-full"
              required
            >
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name || user.username} ({user.username})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Linked URL</label>
            <Input
              value={linkedUrl}
              onChange={(e) => setLinkedUrl(e.target.value)}
              className="input-field"
              placeholder="https://example.com"
            />
          </div>

          <Button type="submit" className="w-full btn-primary" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

// Math Captcha Modal for User Deletion
const MathCaptchaModal = ({ isOpen, onClose, onConfirm, userName }) => {
  const [num1] = useState(Math.floor(Math.random() * 10) + 1);
  const [num2] = useState(Math.floor(Math.random() * 10) + 1);
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseInt(answer) === num1 + num2) {
      setLoading(true);
      try {
        await onConfirm();
        onClose();
      } catch (error) {
        toast.error('Failed to delete user');
      } finally {
        setLoading(false);
      }
    } else {
      toast.error('Incorrect answer! Please try again.');
      setAnswer('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay flex items-center justify-center p-4">
      <motion.div
        className="glass rounded-3xl p-8 w-full max-w-md"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Confirm Deletion</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
            <p className="text-red-400 text-sm">
              ⚠️ Warning: This will permanently delete <strong>{userName}</strong> and ALL their data:
            </p>
            <ul className="text-red-400 text-sm mt-2 ml-4 list-disc">
              <li>Cards</li>
              <li>Profiles</li>
              <li>Orders</li>
              <li>Support tickets</li>
            </ul>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">
              To confirm, solve this math problem:
            </label>
            <div className="bg-slate-800/50 rounded-xl p-4 mb-3">
              <p className="text-2xl font-bold text-white text-center">
                {num1} + {num2} = ?
              </p>
            </div>
            <Input
              type="number"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              className="input-field text-center text-xl"
              placeholder="Your answer"
              autoFocus
              required
            />
          </div>

          <div className="flex gap-3">
            <Button 
              type="button" 
              onClick={onClose} 
              className="flex-1 bg-slate-700 hover:bg-slate-600"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Delete User'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Confirmation Modal for Order Deletion
const ConfirmDeleteModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay flex items-center justify-center p-4">
      <motion.div
        className="glass rounded-3xl p-8 w-full max-w-md"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="mb-6">
          <h2 className="text-xl font-bold text-white mb-3">{title}</h2>
          <p className="text-slate-400">{message}</p>
        </div>

        <div className="flex gap-3">
          <Button 
            type="button" 
            onClick={onClose} 
            className="flex-1 bg-slate-700 hover:bg-slate-600"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            type="button" 
            onClick={handleConfirm}
            className="flex-1 bg-red-600 hover:bg-red-700"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [cards, setCards] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [showCreateCardModal, setShowCreateCardModal] = useState(false);
  const [showEditCardModal, setShowEditCardModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteOrderModal, setShowDeleteOrderModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('metag_admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes, usersRes, cardsRes] = await Promise.all([
        adminAPI.getDashboard(),
        adminAPI.getOrders(),
        adminAPI.getUsers(),
        adminAPI.getCards()
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data || []);
      setUsers(usersRes.data || []);
      setCards(cardsRes.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('metag_admin_token');
        navigate('/admin/login');
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateShipping = async (orderId, data) => {
    await adminAPI.updateShipping(orderId, data);
    toast.success('Order updated successfully');
    fetchData();
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await adminAPI.deleteOrder(orderId);
      toast.success('Order deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const handleSendInvoice = async (orderId) => {
    try {
      await adminAPI.sendInvoice(orderId);
      toast.success('Invoice sent successfully');
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await adminAPI.deleteUser(selectedUser.id);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleCreateCard = async (data) => {
    await adminAPI.createCard(data);
    toast.success('Card created successfully');
    fetchData();
  };

  const handleUpdateCard = async (cardId, data) => {
    await adminAPI.updateCard(cardId, data);
    toast.success('Card updated successfully');
    fetchData();
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this card?')) return;
    try {
      await adminAPI.deleteCard(cardId);
      toast.success('Card deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete card');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('metag_admin_token');
    navigate('/admin/login');
  };

  const handleExportDb = async () => {
    try {
      toast.info('Preparing database export…');
      const response = await adminAPI.exportDb();
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `metag_db_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Database exported successfully!');
    } catch {
      // If blob approach fails, offer the direct URL with token
      const token = localStorage.getItem('metag_admin_token');
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const directUrl = `${apiBase}/admin/export-db?token=${token}`;
      toast.error('Download failed. Opening direct URL…');
      window.open(directUrl, '_blank');
    }
  };

  const downloadInvoice = (orderId) => {
    const token = localStorage.getItem('metag_admin_token');
    const url = `${BACKEND_URL}/api/admin/orders/${orderId}/invoice`;
    
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(response => response.blob())
      .then(blob => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `MeTag_Invoice_${orderId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('Invoice downloaded!');
      })
      .catch(() => toast.error('Failed to download invoice'));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const statusConfig = {
    pending: 'status-pending',
    processing: 'status-processing',
    shipped: 'status-processing',
    in_transit: 'status-processing',
    delivered: 'status-delivered',
    completed: 'status-completed'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="glass-heavy border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">MeTag Admin</h1>
                <p className="text-slate-500 text-sm">Management Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleExportDb}
                variant="outline"
                className="btn-secondary flex items-center gap-2 text-sm"
                data-testid="admin-export-db-btn"
                title="Export full database as ZIP"
              >
                <Download className="w-4 h-4" />
                Export DB
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="btn-secondary flex items-center gap-2"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
          {['overview', 'orders', 'users', 'cards'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-full font-medium capitalize transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-violet-500/20 text-violet-400'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard icon={DollarSign} label="Total Revenue" value={`₹${stats?.totalRevenue?.toLocaleString() || 0}`} color="from-emerald-500 to-teal-600" />
              <StatCard icon={Package} label="Total Orders" value={stats?.totalOrders || 0} color="from-violet-500 to-purple-600" />
              <StatCard icon={Users} label="Total Users" value={stats?.totalUsers || 0} color="from-blue-500 to-cyan-600" />
              <StatCard icon={CreditCard} label="Total Cards" value={stats?.totalCards || 0} color="from-orange-500 to-red-600" />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Truck className="w-5 h-5 text-violet-400" />
                  <h3 className="font-semibold text-white">Pending Shipments</h3>
                </div>
                <p className="text-3xl font-bold text-white">{stats?.pendingShipments || 0}</p>
              </div>
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <h3 className="font-semibold text-white">Delivered Orders</h3>
                </div>
                <p className="text-3xl font-bold text-white">{stats?.deliveredOrders || 0}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-xl font-semibold text-white mb-4">All Orders</h2>
            
            {orders.length === 0 ? (
              <div className="glass rounded-2xl p-12 text-center">
                <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No orders yet</p>
              </div>
            ) : (
              <div className="glass rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Customer</th>
                        <th>Amount</th>
                        <th>Payment</th>
                        <th>Shipping</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id}>
                          <td className="font-mono text-sm">{order.id?.slice(0, 8)}...</td>
                          <td>
                            <div>
                              <p className="text-white">{order.address?.fullName}</p>
                              <p className="text-slate-500 text-sm">{order.address?.email}</p>
                            </div>
                          </td>
                          <td className="text-white">₹{order.discountPrice}</td>
                          <td>
                            <span className={`status-badge ${statusConfig[order.paymentStatus] || 'status-pending'}`}>
                              {order.paymentStatus}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${statusConfig[order.shippingStatus] || 'status-pending'}`}>
                              {order.shippingStatus}
                            </span>
                          </td>
                          <td className="text-slate-400 text-sm">{formatDate(order.createdAt)}</td>
                          <td>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => { setSelectedOrder(order); setShowShippingModal(true); }}
                                variant="outline"
                                size="sm"
                                className="text-xs"
                              >
                                Update
                              </Button>
                              <Button
                                onClick={() => handleSendInvoice(order.id)}
                                variant="outline"
                                size="sm"
                                className="text-xs"
                                title="Send Invoice Email"
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                              <Button
                                onClick={() => { setOrderToDelete(order); setShowDeleteOrderModal(true); }}
                                variant="outline"
                                size="sm"
                                className="text-xs text-red-400 hover:text-red-300"
                                title="Delete Order"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">All Users ({users.length})</h2>
            </div>
            
            {users.length === 0 ? (
              <div className="glass rounded-2xl p-12 text-center">
                <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No users yet</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {users.map((user) => (
                  <div key={user.id} className="glass rounded-2xl p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
                          <User className="w-6 h-6 text-violet-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">{user.name || 'No Name'}</h3>
                          <p className="text-slate-400 text-sm">{user.username}</p>
                          <p className="text-slate-500 text-xs mt-1">Registered: {formatDate(user.createdAt)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-slate-400 text-sm">{user.cardCount || 0} cards</p>
                          {user.profile && (
                            <a 
                              href={`/p/${user.profile.slug}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-violet-400 text-sm flex items-center gap-1 justify-end mt-1 hover:text-violet-300"
                            >
                              View Profile <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <Button
                          onClick={() => { setSelectedUser(user); setShowDeleteUserModal(true); }}
                          variant="outline"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:border-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* User's Cards */}
                    {user.cards && user.cards.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/5">
                        <p className="text-slate-500 text-sm mb-2">Assigned Cards:</p>
                        <div className="flex flex-wrap gap-2">
                          {user.cards.map(card => (
                            <div key={card.id} className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg text-sm">
                              <CreditCard className="w-4 h-4 text-slate-400" />
                              <span className="text-white font-mono">{card.urlCode?.slice(0, 8)}</span>
                              {card.isLinked && <span className="text-emerald-400 text-xs">✓</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Cards Tab */}
        {activeTab === 'cards' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">All Cards ({cards.length})</h2>
              <Button
                onClick={() => setShowCreateCardModal(true)}
                className="btn-primary flex items-center gap-2"
                data-testid="admin-create-card-btn"
              >
                <Plus className="w-4 h-4" />
                Assign New Card
              </Button>
            </div>
            
            {cards.length === 0 ? (
              <div className="glass rounded-2xl p-12 text-center">
                <CreditCard className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No cards yet</p>
              </div>
            ) : (
              <div className="glass rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Card Code</th>
                        <th>Assigned To</th>
                        <th>Linked URL</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cards.map((card) => (
                        <tr key={card.id}>
                          <td>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm text-white">{card.urlCode?.slice(0, 12)}...</span>
                              <button
                                onClick={() => copyToClipboard(card.urlCode)}
                                className="text-slate-400 hover:text-violet-400"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                          <td>
                            <div>
                              <p className="text-white">{card.user?.name || card.user?.username || 'Unknown'}</p>
                              <p className="text-slate-500 text-xs">{card.user?.username}</p>
                            </div>
                          </td>
                          <td>
                            {card.linkedUrl ? (
                              <a 
                                href={card.linkedUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-violet-400 hover:text-violet-300 text-sm flex items-center gap-1"
                              >
                                {card.linkedUrl.slice(0, 30)}...
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <span className="text-slate-500 text-sm">Not linked</span>
                            )}
                          </td>
                          <td>
                            <span className={`status-badge ${card.isLinked ? 'status-completed' : 'status-pending'}`}>
                              {card.isLinked ? 'Linked' : 'Not Linked'}
                            </span>
                          </td>
                          <td className="text-slate-400 text-sm">{formatDate(card.createdAt)}</td>
                          <td>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => { setSelectedCard(card); setShowEditCardModal(true); }}
                                variant="outline"
                                size="sm"
                                className="text-xs"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteCard(card.id)}
                                variant="outline"
                                size="sm"
                                className="text-xs text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </main>

      {/* Modals */}
      <ShippingModal
        isOpen={showShippingModal}
        order={selectedOrder}
        onClose={() => { setShowShippingModal(false); setSelectedOrder(null); }}
        onUpdate={handleUpdateShipping}
      />
      
      <MathCaptchaModal
        isOpen={showDeleteUserModal}
        userName={selectedUser?.name || selectedUser?.username}
        onClose={() => { setShowDeleteUserModal(false); setSelectedUser(null); }}
        onConfirm={handleDeleteUser}
      />
      
      <ConfirmDeleteModal
        isOpen={showDeleteOrderModal}
        title="Delete Order"
        message="Are you sure you want to delete this order? This action cannot be undone."
        onClose={() => { setShowDeleteOrderModal(false); setOrderToDelete(null); }}
        onConfirm={() => handleDeleteOrder(orderToDelete?.id)}
      />
      
      <CreateCardModal
        isOpen={showCreateCardModal}
        users={users}
        onClose={() => setShowCreateCardModal(false)}
        onCreate={handleCreateCard}
      />
      
      <EditCardModal
        isOpen={showEditCardModal}
        card={selectedCard}
        users={users}
        onClose={() => { setShowEditCardModal(false); setSelectedCard(null); }}
        onUpdate={handleUpdateCard}
      />
    </div>
  );
}
